import { useMemo, useState } from 'react';
import { WPPost } from '../../types/wordpress';
import { ClinicalArea, Topic } from '../../types/metadata';
import { clinicalAreaLabels, formatTopicLabel } from '../../utils/metadataHelpers';
import { getPostMetadata } from '../../services/mockMetadata';
import Input from '../ui/Input';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';

interface ReportFiltersProps {
  posts: WPPost[];
  searchQuery: string;
  selectedYears: string[];
  selectedTopics: Topic[];
  selectedClinicalAreas: ClinicalArea[];
  onSearchChange: (query: string) => void;
  onYearToggle: (year: string) => void;
  onTopicToggle: (topic: Topic) => void;
  onClinicalAreaToggle: (area: ClinicalArea) => void;
  onClear: () => void;
}

const TOPIC_PREVIEW = 20;

function countsFor<T extends string>(posts: WPPost[], pick: (post: WPPost) => T[] | undefined): Map<T, number> {
  const counts = new Map<T, number>();
  for (const post of posts) {
    for (const value of pick(post) ?? []) {
      counts.set(value, (counts.get(value) ?? 0) + 1);
    }
  }
  return counts;
}

function postYear(post: WPPost): string | null {
  const year = new Date(post.date).getFullYear();
  return Number.isFinite(year) ? String(year) : null;
}

export default function ReportFilters({
  posts,
  searchQuery,
  selectedYears,
  selectedTopics,
  selectedClinicalAreas,
  onSearchChange,
  onYearToggle,
  onTopicToggle,
  onClinicalAreaToggle,
  onClear,
}: ReportFiltersProps) {
  const [yearsOpen, setYearsOpen] = useState(true);
  const [topicsOpen, setTopicsOpen] = useState(true);
  const [areasOpen, setAreasOpen] = useState(true);
  const [showAllTopics, setShowAllTopics] = useState(false);

  const topicCounts = useMemo(
    () => countsFor(posts, (post) => getPostMetadata(post).topics),
    [posts]
  );
  const areaCounts = useMemo(
    () => countsFor(posts, (post) => getPostMetadata(post).clinicalAreas),
    [posts]
  );

  const topics = useMemo(
    () =>
      [...topicCounts.entries()].sort(
        (a, b) => b[1] - a[1] || formatTopicLabel(a[0]).localeCompare(formatTopicLabel(b[0]))
      ),
    [topicCounts]
  );
  const areas = useMemo(
    () =>
      [...areaCounts.entries()].sort(
        (a, b) => b[1] - a[1] || clinicalAreaLabels[a[0]].localeCompare(clinicalAreaLabels[b[0]])
      ),
    [areaCounts]
  );

  const years = useMemo(() => {
    const counts = new Map<string, number>();
    for (const post of posts) {
      const year = postYear(post);
      if (!year) continue;
      counts.set(year, (counts.get(year) ?? 0) + 1);
    }
    return [...counts.entries()].sort((a, b) => b[0].localeCompare(a[0]));
  }, [posts]);

  const visibleTopics = showAllTopics ? topics : topics.slice(0, TOPIC_PREVIEW);
  const hasFilters =
    searchQuery.trim().length > 0 ||
    selectedYears.length > 0 ||
    selectedTopics.length > 0 ||
    selectedClinicalAreas.length > 0;

  const chipClass = (selected: boolean) =>
    `inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
      selected ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
    }`;

  return (
    <aside className="w-full lg:w-80 space-y-4 lg:max-h-[calc(100vh-8rem)] lg:overflow-y-auto lg:pr-2">
      <div>
        <label htmlFor="report-search" className="block text-sm font-medium text-gray-700 mb-2">
          Search
        </label>
        <Input
          id="report-search"
          type="search"
          placeholder="Search articles..."
          value={searchQuery}
          onChange={(event) => onSearchChange(event.target.value)}
        />
      </div>

      <div className="border-b border-gray-200 pb-4">
        <button
          type="button"
          onClick={() => setYearsOpen((open) => !open)}
          className="flex items-center justify-between w-full mb-3 min-h-[44px]"
          aria-expanded={yearsOpen}
        >
          <span className="text-sm font-medium text-gray-700">
            Year
            {selectedYears.length > 0 && (
              <span className="ml-2 text-xs bg-primary text-white rounded-full px-2 py-0.5">
                {selectedYears.length}
              </span>
            )}
          </span>
          {yearsOpen ? (
            <ChevronUpIcon className="h-5 w-5 text-gray-500" />
          ) : (
            <ChevronDownIcon className="h-5 w-5 text-gray-500" />
          )}
        </button>
        {yearsOpen && (
          <div className="flex flex-wrap gap-2">
            {years.map(([year, count]) => (
              <button
                key={year}
                type="button"
                onClick={() => onYearToggle(year)}
                className={chipClass(selectedYears.includes(year))}
                aria-pressed={selectedYears.includes(year)}
              >
                {year}
                <span className={selectedYears.includes(year) ? 'text-white/80' : 'text-gray-500'}>
                  {count}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      <p className="text-sm text-gray-500">
        Sample tags until the resource review is loaded.
      </p>
      {hasFilters && (
        <button
          type="button"
          onClick={onClear}
          className="min-h-[44px] text-sm font-medium text-primary hover:text-primary-dark focus-ring rounded-sm"
        >
          Clear filters
        </button>
      )}

      <div className="border-b border-gray-200 pb-4">
        <button
          type="button"
          onClick={() => setTopicsOpen((open) => !open)}
          className="flex items-center justify-between w-full mb-3 min-h-[44px]"
          aria-expanded={topicsOpen}
        >
          <span className="text-sm font-medium text-gray-700">
            Topics
            {selectedTopics.length > 0 && (
              <span className="ml-2 text-xs bg-primary text-white rounded-full px-2 py-0.5">
                {selectedTopics.length}
              </span>
            )}
          </span>
          {topicsOpen ? (
            <ChevronUpIcon className="h-5 w-5 text-gray-500" />
          ) : (
            <ChevronDownIcon className="h-5 w-5 text-gray-500" />
          )}
        </button>
        {topicsOpen && (
          <div className="flex flex-wrap gap-2">
            {visibleTopics.map(([topic, count]) => (
              <button
                key={topic}
                type="button"
                onClick={() => onTopicToggle(topic)}
                className={chipClass(selectedTopics.includes(topic))}
                aria-pressed={selectedTopics.includes(topic)}
              >
                {formatTopicLabel(topic)}
                <span className={selectedTopics.includes(topic) ? 'text-white/80' : 'text-gray-500'}>
                  {count}
                </span>
              </button>
            ))}
            {topics.length > TOPIC_PREVIEW && (
              <button
                type="button"
                onClick={() => setShowAllTopics((show) => !show)}
                className="text-sm text-primary hover:text-primary-dark font-medium w-full text-left pt-2 min-h-[44px]"
              >
                {showAllTopics ? 'Show less' : `Show all ${topics.length} topics`}
              </button>
            )}
          </div>
        )}
      </div>

      <div className="border-b border-gray-200 pb-4">
        <button
          type="button"
          onClick={() => setAreasOpen((open) => !open)}
          className="flex items-center justify-between w-full mb-3 min-h-[44px]"
          aria-expanded={areasOpen}
        >
          <span className="text-sm font-medium text-gray-700">
            Clinical Area
            {selectedClinicalAreas.length > 0 && (
              <span className="ml-2 text-xs bg-primary text-white rounded-full px-2 py-0.5">
                {selectedClinicalAreas.length}
              </span>
            )}
          </span>
          {areasOpen ? (
            <ChevronUpIcon className="h-5 w-5 text-gray-500" />
          ) : (
            <ChevronDownIcon className="h-5 w-5 text-gray-500" />
          )}
        </button>
        {areasOpen && (
          <div className="flex flex-wrap gap-2">
            {areas.map(([area, count]) => (
              <button
                key={area}
                type="button"
                onClick={() => onClinicalAreaToggle(area)}
                className={chipClass(selectedClinicalAreas.includes(area))}
                aria-pressed={selectedClinicalAreas.includes(area)}
              >
                {clinicalAreaLabels[area]}
                <span className={selectedClinicalAreas.includes(area) ? 'text-white/80' : 'text-gray-500'}>
                  {count}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </aside>
  );
}
